import { useQuery } from '@tanstack/react-query';
import { fetchJSON } from '../lib/api-client';
import { isDelistedError, DELISTED_MESSAGE } from '../lib/error-utils';

interface EarningsHistoryItem {
  date: string;
  epsActual?: number;
  epsEstimate?: number;
  surprisePct?: number;
  gapNextDayPct?: number;
  moveT1dPct?: number;
}

interface EarningsFacts {
  symbol: string;
  asOf: string;
  nextEarningsDate?: string;
  daysToEarnings?: number;
  consensus?: { eps?: number; revenue?: number };
  history: EarningsHistoryItem[];
  earningsDayMoveAvg?: number;
  earningsDayMoveMedian?: number;
  dataQuality: { warnings: string[]; historyCount: number; gapHistoryCount: number };
}

interface EarningsScenario {
  name: 'gap-up' | 'gap-down' | 'flat-open';
  condition: string;
  stance: string;
  entry?: number;
  stop?: number;
  t1?: number;
  t2?: number;
  trigger: string;
  invalidation: string;
}

interface EarningsPlaybook {
  decisionSummary: string;
  expectedMovePct?: number;
  currentPrice?: number;
  scenarios: EarningsScenario[];
  warnings: string[];
  mode: 'facts-only';
}

function pct(n: number | undefined, digits = 1): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${(n * 100).toFixed(digits)}%`;
}

function num(n: number | undefined, digits = 2): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return n.toFixed(digits);
}

function dateLabel(value: string | undefined): string {
  if (!value) return 'unknown';
  const d = new Date(`${value}T00:00:00`);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('zh-TW') : value;
}

export function EarningsCockpit({ symbol }: { symbol: string }) {
  const factsQ = useQuery({
    queryKey: ['earnings-facts', symbol],
    queryFn: () => fetchJSON<EarningsFacts>(`/api/earnings/${encodeURIComponent(symbol)}`),
    enabled: !!symbol,
    staleTime: 30 * 60_000,
    retry: 1,
  });
  const playbookQ = useQuery({
    queryKey: ['earnings-playbook', symbol],
    queryFn: () => fetchJSON<EarningsPlaybook>(`/api/earnings/${encodeURIComponent(symbol)}/playbook`),
    enabled: !!symbol && !!factsQ.data,
    staleTime: 30 * 60_000,
    retry: 1,
  });

  if (factsQ.isLoading) {
    return <div className="card"><div className="text-(--text-muted) text-sm py-4 text-center">載入財報 evidence…</div></div>;
  }
  if (factsQ.error) {
    const err = factsQ.error as Error;
    return <div className="card"><div className={isDelistedError(err) ? 'text-(--text-muted) text-sm' : 'text-(--up) text-sm'}>{isDelistedError(err) ? DELISTED_MESSAGE : `無法取得財報 evidence：${err.message}`}</div></div>;
  }
  if (!factsQ.data) return null;

  const facts = factsQ.data;
  const playbook = playbookQ.data;
  const warnings = Array.from(new Set([...facts.dataQuality.warnings, ...(playbook?.warnings ?? [])]));

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="font-bold text-(--text-primary) text-sm">📅 財報依據</div>
          <div className="text-[10px] text-(--text-muted) tnum">{facts.symbol} · {new Date(facts.asOf).toLocaleString('zh-TW', { hour12: false })}</div>
        </div>
        <span className="rounded border border-(--border) px-1.5 py-0.5 text-[10px] text-(--text-muted)">純資料</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <Stat label="下次財報" value={dateLabel(facts.nextEarningsDate)} accent={facts.daysToEarnings != null && facts.daysToEarnings <= 3 ? 'down' : 'flat'} />
        <Stat label="倒數" value={facts.daysToEarnings != null ? `${facts.daysToEarnings} 天` : 'unknown'} />
        <Stat label="EPS 預估" value={num(facts.consensus?.eps)} />
        <Stat label="平均波動" value={pct(facts.earningsDayMoveAvg)} accent={facts.earningsDayMoveAvg != null && facts.earningsDayMoveAvg > 0.06 ? 'down' : 'flat'} />
      </div>

      {playbook && (
        <div className="rounded border border-(--accent)/30 bg-(--accent)/4 p-2 mb-3 text-[12px] text-(--text-secondary) leading-relaxed">
          <div className="font-bold text-(--text-primary) mb-1">決策摘要：{playbook.decisionSummary}</div>
          <div className="text-[10px] text-(--text-muted) tnum">
            現價 {num(playbook.currentPrice)} · 預期波動 {pct(playbook.expectedMovePct)} · 純資料
          </div>
        </div>
      )}

      {playbook?.scenarios && playbook.scenarios.length > 0 && (
        <div className="space-y-2 mb-3">
          {playbook.scenarios.map((s) => (
            <details key={s.name} className="rounded border border-(--border) bg-(--bg-base) px-2 py-1.5">
              <summary className="cursor-pointer text-[11px] font-bold text-(--text-primary)">{scenarioLabel(s.name)} · {stanceLabel(s.stance)}</summary>
              <div className="mt-1.5 text-[11px] text-(--text-secondary) leading-relaxed space-y-1">
                <div>{s.condition}</div>
                <div className="tnum">進 / 停 / T1 / T2：{num(s.entry)} / {num(s.stop)} / {num(s.t1)} / {num(s.t2)}</div>
                <div>觸發：{s.trigger}</div>
                <div>放棄：{s.invalidation}</div>
              </div>
            </details>
          ))}
        </div>
      )}

      {facts.history.length > 0 && (
        <table className="w-full text-[11px] tnum mb-3">
          <thead>
            <tr className="text-(--text-muted) text-left border-b border-(--border)">
              <th className="py-0.5 pr-2">日期</th>
              <th className="py-0.5 pr-2 text-right">EPS</th>
              <th className="py-0.5 pr-2 text-right">驚喜</th>
              <th className="py-0.5 pr-2 text-right">隔日</th>
            </tr>
          </thead>
          <tbody>
            {facts.history.slice(0, 5).map((h, i) => (
              <tr key={`${h.date}:${i}`}>
                <td className="py-0.5 pr-2">{dateLabel(h.date)}</td>
                <td className="py-0.5 pr-2 text-right">{num(h.epsActual)} / {num(h.epsEstimate)}</td>
                <td className={`py-0.5 pr-2 text-right ${h.surprisePct != null && h.surprisePct > 0 ? 'text-(--up)' : h.surprisePct != null && h.surprisePct < 0 ? 'text-(--down)' : ''}`}>{pct(h.surprisePct)}</td>
                <td className={`py-0.5 pr-2 text-right ${h.moveT1dPct != null && h.moveT1dPct > 0 ? 'text-(--up)' : h.moveT1dPct != null && h.moveT1dPct < 0 ? 'text-(--down)' : ''}`}>{pct(h.moveT1dPct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {warnings.length > 0 && (
        <div className="text-[10px] text-(--warn) leading-relaxed">
          {warnings.slice(0, 3).map((w) => <div key={w}>⚠ {w}</div>)}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = 'flat' }: { label: string; value: string; accent?: 'up' | 'down' | 'flat' }) {
  const cls = accent === 'up' ? 'text-(--up)' : accent === 'down' ? 'text-(--down)' : 'text-(--text-primary)';
  return (
    <div className="rounded bg-(--bg-base) border border-(--border) px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-(--text-muted)">{label}</div>
      <div className={`text-xs font-bold tnum ${cls}`}>{value}</div>
    </div>
  );
}

function scenarioLabel(name: EarningsScenario['name']): string {
  if (name === 'gap-up') return '跳空開高';
  if (name === 'gap-down') return '跳空開低';
  return '平盤開出';
}

function stanceLabel(stance: string): string {
  if (stance === 'long-after-confirmation') return '確認後做多';
  if (stance === 'short-or-avoid') return '偏空或避開';
  return '等方向';
}
